import {AxiosResponse} from "axios"
import {PagedData} from "@/services/models/shared/PagedData";
import {
    NewPersonOrganizationRole,
    Organization,
    OrganizationFilterOptions,
    OrganizationListItem,
    PersonOrganizationRole,
    OrganizationRole,
    UpdateOrganization,
    UpdatePersonOrganizationRole
} from "@/services/models/Organization";
import BimsHttpService from "@/services/BimsHttpService";

class OrganizationService {

    /**
     * @description Returns a paged list of organizations.
     */
    getOrganizationList(filter: OrganizationFilterOptions): Promise<PagedData<OrganizationListItem>> {
        return BimsHttpService.get(`/organizations`, {
            params: filter
        }).then((response: AxiosResponse<PagedData<OrganizationListItem>>) => {
            return response.data as PagedData<OrganizationListItem>;
        });
    }

    /**
     *  @description Retrieves all organizations
     */
    getAllOrganizations(): Promise<OrganizationListItem[]> {
        const vm = this;
        return vm.getOrganizationList({
            returnAll: false,
            onlyShowActive: true,
            pageSize: 1
        } as OrganizationFilterOptions)
            .then((response) => {
                return vm.getOrganizationList({
                    returnAll: false,
                    onlyShowActive: true,
                    pageSize: response.totalCount,
                    page: 1
                } as OrganizationFilterOptions)
                    .then((responses) => {
                        return responses.data;
                    })
            })
    }

    /**
     * @description Returns the organization with the specified id
     */
    getOrganization(id: string): Promise<Organization> {
        return BimsHttpService.get(`/organizations/${id}`).then((response: AxiosResponse<Organization>) => {
            return response.data as Organization;
        });
    }

    /**
     * @description Returns list of organization business roles that can be assigned to an person in an organization
     */
    getOrganizationBusinessRoles(): Promise<OrganizationRole[]> {
      return BimsHttpService.get(`/organizations/roles`, { params: { onlyStaffRoles: false } }).then((response: AxiosResponse<OrganizationRole[]>) => {
        return response.data;
    });
    }

    /**
     * @description Returns all roles for the specified organization
     */
    getOrganizationRoles(organizationId: string, onlyStaffRoles?: boolean | null): Promise<PersonOrganizationRole[]> {
        return BimsHttpService.get(`/organizations/${organizationId}/roles`, { params: { onlyStaffRoles: onlyStaffRoles } }).then((response: AxiosResponse<PersonOrganizationRole[]>) => {
            return response.data;
        });
    }

    /**
     *  @description Updates the general information for an organization
     */
    updateOrganization(organization: UpdateOrganization): Promise<any> {
        return BimsHttpService.put("/organizations/" + organization.id,
            organization
        )
    }

    /**
     *  @description Adds a new organization role for the specified organization
     */
    addOrganizationRole(personOrganizationRole: NewPersonOrganizationRole): Promise<any> {
        return BimsHttpService.post(`/organizations/${personOrganizationRole.organizationId}/roles`,
            personOrganizationRole
        );
    }

    /**
     * @description Updates the organization role
     */
    updateOrganizationRole(personOrganizationRole: UpdatePersonOrganizationRole): Promise<any> {
        return BimsHttpService.put(`/organizations/roles/${personOrganizationRole.id}`,
            personOrganizationRole
        )
    }

    /**
     *  @description Deletes the organization role with the specified id
     */
    deleteOrganizationRole(id: string): Promise<any> {
        return BimsHttpService.delete(`/organizations/roles/${id}`)
    }

    /**
     * @description Downloads the excel export of organizations' business roles
     */
    public getExcelExport(asExcel: boolean): Promise<any> {
        return BimsHttpService({
          method: "GET",
          responseType: "blob",
          url: `organizations/roles/business/export?asExcel=${asExcel}`,
        }).then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
    
          link.setAttribute("download", (response.headers['content-disposition'].split('filename=')[1].split(';')[0]).replace(/['"]+/g, ''));
          document.body.appendChild(link);
          link.click();
    
          // Remove the child after the doanload has completed
          document.body.removeChild(link);
        });
    }
}

export default new OrganizationService();
